import type { BackendUser } from '../user/userService';
import {
  canAccessRecipesPage, canAccessInventoryPage, canAccessMepPage,
  canAccessChecklistsPage, canAccessNotificationsPage, canAccessWastePage,
  canAccessStaffPage,
} from './accessManagement';

export const MAIN_PAGE_RESTRICTION = 'restrict_main_pages';
export const STAFF_HOME_PATH = '/staff-home';

/** A restriction is explicit and is never implied by all_permissions. */
export const hasMainPageRestriction = (user: Pick<BackendUser, 'role' | 'permissions'> | null): boolean =>
  user?.role === 'staff' && Boolean(user.permissions?.includes(MAIN_PAGE_RESTRICTION));

const operationalPages = [
  { path: '/recipes', label: 'nav.recipes', canAccess: canAccessRecipesPage },
  { path: '/inventory', label: 'nav.inventory', canAccess: canAccessInventoryPage },
  { path: '/mep', label: 'nav.mep', canAccess: canAccessMepPage },
  { path: '/checklists', label: 'nav.checklists', canAccess: canAccessChecklistsPage },
  { path: '/notifications', label: 'nav.notifications', canAccess: canAccessNotificationsPage },
  { path: '/waste', label: 'nav.wasteTracking', canAccess: canAccessWastePage },
  { path: '/staff', label: 'nav.staff', canAccess: canAccessStaffPage },
];

export const getOperationalPages = (user: BackendUser | null, accountSettings: string[]) =>
  operationalPages.filter(page => page.canAccess(user, accountSettings));

export const getRestrictedLandingPath = (user: BackendUser | null, accountSettings: string[]): string =>
  getOperationalPages(user, accountSettings)[0]?.path ?? STAFF_HOME_PATH;

const blockedMainPages = [
  '/dashboard', '/orderhistory', '/revenuehistory', '/pricealerts', '/crossrestaurantprices',
  '/costprice', '/costpercent', '/analysis', '/charts', '/reports', '/reports-legacy', '/utilization',
  '/menus', '/invoices', '/testinvoices', '/home',
];
const isRouteFamily = (path: string, root: string) => path === root || path.startsWith(`${root}/`);

/** React Router is case-insensitive; apply the same policy to direct links. */
export function getMainPageRestrictionRedirect(
  pathname: string, user: BackendUser | null, accountSettings: string[]
): string | null {
  if (!hasMainPageRestriction(user)) return null;
  let decodedPath = pathname;
  try { decodedPath = decodeURIComponent(pathname); } catch { /* Malformed paths cannot match an encoded route. */ }
  const path = decodedPath.toLowerCase().replace(/\/+$/, '') || '/';
  const landing = getRestrictedLandingPath(user, accountSettings);
  if (path === '/' || blockedMainPages.some(root => isRouteFamily(path, root))) return landing;
  const operational = operationalPages.find(page => isRouteFamily(path, page.path))
    ?? (isRouteFamily(path, '/recipe-viewer') ? operationalPages[0] : undefined);
  if (operational && !operational.canAccess(user, accountSettings)) return landing;
  if (path === STAFF_HOME_PATH && landing !== STAFF_HOME_PATH) return landing;
  return null;
}

/** Preserve restriction state through the existing All permissions replacement behavior. */
export function toggleStaffPermission(permissions: string[] | null | undefined, permission: string, enabled: boolean): string[] {
  const current = permissions ?? [];
  if (permission === MAIN_PAGE_RESTRICTION) {
    return enabled ? Array.from(new Set([...current, permission])) : current.filter(p => p !== permission);
  }
  if (permission === 'all_permissions') {
    return [...(enabled ? ['all_permissions'] : []), ...current.filter(p => p === MAIN_PAGE_RESTRICTION)];
  }
  const individual = current.filter(p => p !== 'all_permissions' && p !== permission);
  return enabled ? [...individual, permission] : individual;
}
